/**
 * Custom gift management API with its rules deriving from the `gifts-with-purchase` section.
 *
 * Supports two promo shapes:
 *   - "subtotal" (choose-1-of-N): promo.giftOptions = [{ productId, giftVariantId, ... }, ...].
 *     The customer may have at most `giftQuantity` gifts across any combination of options.
 *   - Legacy single-product (BXGY, free samples): promo.productId / promo.giftVariantId, plus
 *     optional promo.requiredPurchase[].
 */
class GiftManager {
  /**
   * @param {Array<Object>} promos - Promotional offers to initialize the manager with.
   * @throws {Error} If a promo's required configuration is missing.
   */
  constructor(promos) {
    this.promos = [];
    promos.forEach((promo) => {
      if (!promo.id) {
        throw new Error('Invalid promo: configuration requires an identifier.');
      }
      if (!Number.isInteger(promo.giftQuantity)) {
        throw new Error(
          `Invalid promo: ${promo.id} requires a valid gift quantity number.`,
        );
      }
      if (!Number.isInteger(promo.minSubtotal)) {
        throw new Error(
          `Invalid promo: ${promo.id} requires a minimum subtotal number.`,
        );
      }

      const hasOptions =
        Array.isArray(promo.giftOptions) && promo.giftOptions.length > 0;

      if (hasOptions) {
        const allOptionsValid = promo.giftOptions.every(
          (option) =>
            Number.isInteger(option.productId) &&
            Number.isInteger(option.giftVariantId),
        );
        if (!allOptionsValid) {
          throw new Error(
            `Invalid promo: ${promo.id} has gift options missing productId or giftVariantId.`,
          );
        }
      } else if (!Number.isInteger(promo.giftVariantId)) {
        throw new Error(
          `Invalid promo: ${promo.id} requires a gift variant identifier number.`,
        );
      }

      this.promos.push({ ...promo });
    });
  }

  /**
   * Silently runs the GWP logic. Invalid gifts are removed from the cart, and any over-selected
   * gifts within a single promo are pruned down to that promo's giftQuantity. When done, a
   * `gift-manager:update` event is dispatched detailing all eligible promos for the latest cart.
   * @param {Object} cart - The Shopify cart object.
   * @returns {Promise<void>}
   */
  run(cart) {
    const giftLineItems = cart.items.filter((item) => {
      if (item.discounts.length && item.properties.Gift) {
        // Some gifts may be discounted to $0 and will need to be evaluated.
        return true;
      }
      return (
        item.price === 0 &&
        !item.properties._group &&
        !item.properties._bundle_id &&
        !item.properties._swell_redemption_token &&
        !item.properties._rc_bundle
      );
    });

    const purgeGifts = {};

    // Group gift line items by their promo id so we can enforce gift quantity per promo.
    const itemsByPromo = giftLineItems.reduce((acc, item) => {
      const promoId = item.properties?._promo_id;
      if (!promoId) return acc;
      acc[promoId] = acc[promoId] || [];
      acc[promoId].push(item);
      return acc;
    }, {});

    giftLineItems.forEach((item) => {
      const promoId = item.properties?._promo_id;
      if (!promoId) {
        // Not a managed gift; leave it alone.
        return;
      }
      const promo = this.promos.find((p) => p.id === promoId);
      if (!promo) {
        // Orphaned gift — promo no longer exists.
        purgeGifts[item.key] = 0;
        return;
      }
      if (!this.promoIsValid(cart, promo)) {
        // Promo no longer eligible for this cart.
        purgeGifts[item.key] = 0;
        return;
      }

      // For multi-option promos, ensure the gift line is one of the promo's allowed variants.
      if (Array.isArray(promo.giftOptions) && promo.giftOptions.length > 0) {
        const matchesOption = promo.giftOptions.some(
          (option) =>
            option.giftVariantId === item.variant_id ||
            option.productId === item.product_id,
        );
        if (!matchesOption) {
          purgeGifts[item.key] = 0;
        }
      }
    });

    // Enforce per-promo gift quantity. If a promo has accumulated more gifts than allowed
    // (e.g. a click race in flight), keep the most recent line and purge the rest.
    Object.entries(itemsByPromo).forEach(([promoId, items]) => {
      const promo = this.promos.find((p) => p.id === promoId);
      if (!promo) return;
      const max = promo.giftQuantity || 1;
      // Shopify cart preserves insertion order; the most recently added is last.
      const surplus = items.slice(0, Math.max(0, items.length - max));
      surplus.forEach((item) => {
        purgeGifts[item.key] = 0;
      });
    });

    return window.cart.update(purgeGifts, true).then((latestCart) => {
      const eligiblePromos = this.promos.filter((promo) =>
        this.promoIsValid(latestCart, promo),
      );
      document.dispatchEvent(
        new CustomEvent('gift-manager:update', {
          detail: { cart: latestCart, eligiblePromos },
        }),
      );
    });
  }

  /**
   * Checks if a promotional offer is valid based on the contents of the cart.
   * @param {Object} cart - The Shopify cart object.
   * @param {Object} promo - The promotional offer object to be validated.
   * @returns {boolean} True if the promotional offer is valid; otherwise, false.
   */
  promoIsValid(cart, promo) {
    // Quantity ceiling: total of any product the customer could've claimed for this promo.
    const eligibleProductIds =
      Array.isArray(promo.giftOptions) && promo.giftOptions.length > 0
        ? promo.giftOptions.map((option) => option.productId)
        : promo.productId
          ? [promo.productId]
          : [];

    if (eligibleProductIds.length) {
      const totalQuantity = cart.items.reduce((sum, item) => {
        if (eligibleProductIds.indexOf(item.product_id) > -1) {
          return sum + item.quantity;
        }
        return sum;
      }, 0);
      if (totalQuantity > promo.giftQuantity) {
        return false;
      }
    }

    if (promo.minSubtotal > cart.total_price) {
      return false;
    }

    if (promo.requiredPurchase?.length) {
      for (let i = 0; i < cart.items.length; i++) {
        const cartItem = cart.items[i];
        if (promo.requiredPurchase.indexOf(cartItem.product_id) > -1) {
          return true;
        }
      }
      return false;
    }

    return true;
  }
}

window.GiftManager = GiftManager;
